import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import DataComponent from '../../../DataComponent/DataComponent';
import {isMobile} from "utils/DeviceDetect";
import { useI18n } from 'utils/I18n/context';

const ConversionList = ({ 
    totalPages: propTotalPages, 
    totalItems: propTotalItems, 
    page: propPage, 
    loading: propLoading,
    headerNames,
    list: propList,
    statuses,
    actionTypes,
    actionNames,
    getConversions,
    getTotalRows
}) => {
    const { t } = useI18n();

    const generateSessionId = useCallback((len, charSet) => {
        charSet = charSet || 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        var randomString = '';
        for (var i = 0; i < len; i++) {
            var randomPoz = Math.floor(Math.random() * charSet.length);
            randomString += charSet.substring(randomPoz,randomPoz+1);
        }
        return randomString;
    }, []);

    const [list, setList] = useState([]);
    const [totalPages, setTotalPages] = useState(propTotalPages);
    const [totalItems, setTotalItems] = useState(propTotalItems);
    const [page, setPage] = useState(propPage);
    const [loading, setLoading] = useState(propLoading);
    const [sessionId] = useState(() => generateSessionId(32));

    const staticNameTable = t?.dataTable || {};
    const staticNameConversions = t?.conversionPage?.conversions || {};

    useEffect(() => {
        if (getTotalRows) {
            getTotalRows({ sessionId });
        }
        if (getConversions) {
            getConversions({ sessionId });
        }
    }, [getTotalRows, getConversions, sessionId]);

    useEffect(() => {
        if (propList && headerNames) {
            const filteredList = [];
            for (let conversion of propList) {
                let conversionFiltered = {};
                let pushConversions = false;
                for (let dataField in conversion) {
                    const header = headerNames.find(header => header.keyTitle === dataField);
                    if (header && header.visible) {
                        conversionFiltered[dataField] = conversion[dataField];
                        if (!pushConversions) {
                            pushConversions = true;
                        }
                    }
                }
                if (pushConversions) {
                    filteredList.push(conversionFiltered);
                }
            }

            setList(filteredList);
            setTotalPages(propTotalPages);
            setTotalItems(propTotalItems);
            setPage(propPage);
            const hasServerTotals = (typeof propTotalPages === 'number') && (typeof propTotalItems === 'number');
            setLoading(propLoading && !hasServerTotals);
        }
    }, [propList, propTotalPages, propTotalItems, propPage, propLoading, headerNames]);

    const onDataTableAction = useCallback((state, actionType) => {
        if (actionType === 'firstRender') return;
        if (getConversions) {
            const stateWithSession = { ...state, sessionId };
            getConversions(stateWithSession);
        }
    }, [getConversions, sessionId]);

    const onDataTableActionFilter = useCallback((state, actionType) => {
        if (actionType === 'firstRender') return;
        if (getTotalRows) {
            const stateWithSession = { ...state, sessionId };
            getTotalRows(stateWithSession);
        }
    }, [getTotalRows, sessionId]);

    return (
        <div className={`history-postback__table ${isMobile ? 'mobile-view' : ''}`}>
            <DataComponent
                onAction={onDataTableAction}
                onActionFilter={onDataTableActionFilter}
                totalPages={totalPages}
                totalRows={totalItems}
                headerNames={headerNames}
                headerNamesArray={false}
                autoTile={true}
                data={list}
                loading={loading}
                sessionId={sessionId}
                isQuery={true}
                settings={true}
                settingKey={'conversions'}
                tableBodyTitle={staticNameConversions}
                className="simple-table green-hover"
                gray={true}
                fields={[ 
                    {
                        name: 'datepicker',
                        key: 'create',
                        label: staticNameTable?.headers?.createdAt,
                        dropdownLabel: staticNameTable?.filter
                    },
                    {
                        name: 'datepicker',
                        key: 'updated',
                        label: staticNameTable?.headers?.updatedAt,
                        dropdownLabel: staticNameTable?.filter,
                    },
                    {
                        name: 'datepicker',
                        key: 'trackСreated',
                        label: staticNameTable?.headers?.createdAtTrack,
                        dropdownLabel: staticNameTable?.filter,
                    },
                    {
                        name: 'dynamic',
                        key: 'offer',
                        segment: 'Offer',
                        label: staticNameTable?.headers?.offerTitle,
                        dropdownLabel: staticNameTable?.filter,
                        withList: true
                    },
                    {
                        name: 'goalTree',
                        key: 'goals',
                        nesting: 'goals',
                        label: staticNameTable?.headers?.goalTitle,
                        dropdownLabel: staticNameTable?.filter,
                    },
                    {
                        name: 'goalTree',
                        key: 'goalActions',
                        label: staticNameTable?.headers?.goalActions,
                        dropdownLabel: staticNameTable?.filter,
                    },
                    {
                        name: 'multiselect',
                        key: 'statuses',
                        data: statuses,
                        placeholder: staticNameTable?.tips?.byStatus,
                        label: staticNameTable?.headers?.status,
                        dropdownLabel: staticNameTable?.filter,
                    },
                    {
                        name: 'multiselect',
                        key: 'actionTypes',
                        data: actionTypes,
                        placeholder: staticNameTable?.tips?.byActionType,
                        label: staticNameTable?.headers?.typeAction,
                        dropdownLabel: staticNameTable?.filter,
                    },
                    {
                        name: 'multiselect',
                        key: 'actionNames',
                        data: actionNames,
                        placeholder: staticNameTable?.tips?.byActionName,
                        label: staticNameTable?.headers?.nameAction,
                        dropdownLabel: staticNameTable?.filter,
                    },
                    {
                        name: 'dynamic',
                        key: 'publisher',
                        segment: 'Publisher',
                        label: staticNameTable?.headers?.publisher,
                        dropdownLabel: staticNameTable?.filter,
                        withList: true
                    },
                    {name: 'custom', key: 'advCids', label: staticNameTable?.headers?.advCid, dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'utmMedium', label: 'UtmMedium', dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'utmSource', label: 'UtmSource', dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'utmCampaign', label: 'UtmCampaign', dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'utmTerm', label: 'UtmTerm', dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'utmContent', label: 'UtmContent', dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'param1', label: 'Param1', dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'param2', label: 'Param2', dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'param3', label: 'Param3', dropdownLabel: staticNameTable?.filter},
                    {name: 'custom', key: 'param4', label: 'Param4', dropdownLabel: staticNameTable?.filter},
                ]}
            />
        </div>
    );
};

ConversionList.propTypes = {
    totalPages: PropTypes.number,
    totalItems: PropTypes.number,
    page: PropTypes.number,
    loading: PropTypes.bool,
    headerNames: PropTypes.array,
    list: PropTypes.array,
    statuses: PropTypes.array,
    actionTypes: PropTypes.array,
    actionNames: PropTypes.array,
    getConversions: PropTypes.func,
    getTotalRows: PropTypes.func
};

ConversionList.defaultProps = {
    totalPages: 0,
    totalItems: 0,
    page: 1,
    loading: false,
    headerNames: [],
    list: [],
    statuses: [],
    actionTypes: [],
    actionNames: [],
    getConversions: () => {},
    getTotalRows: () => {}
};

export default ConversionList;